/**
 * OIDC client for the central IdP (auth.apiboo.com). Desktop login is the
 * standard native-app flow: Authorization Code + PKCE through the system
 * browser, back via the Rust loopback listener (oauth.rs), token exchange via
 * Rust reqwest (no CORS). The app is a public client — no secret.
 */

import { endWebSession } from './webSession';
import axios from 'axios';
import { tauriAxiosAdapter } from '../utils/tauriAxiosAdapter';
import {
  generateCodeVerifier,
  computeCodeChallengeS256,
  generateOAuthState,
  buildAuthorizeUrl,
  defaultRedirectUri,
  expiresAtFromExpiresIn,
} from '../utils/oauthPkce';
import { isWebApp } from '../platform/target';
import { refreshWeb } from './webOidc';

export const AUTH_BASE = (
  process.env.REACT_APP_AUTH_BASE_URL || 'https://auth.apiboo.com'
).replace(/\/+$/, '');

// Fixed first-party client id, seeded by apiboo-auth (priv/repo/seeds.exs).
export const OIDC_CLIENT_ID =
  process.env.REACT_APP_AUTH_CLIENT_ID || '019817d2-0a7e-7c4a-b7e3-a71b32a0e3d1';

// `apiboo.api` is what the main API requires; `offline_access` yields the
// refresh token that carries the session between app launches.
const SCOPE = 'openid profile email offline_access apiboo.api';

// Plain HTTP client routed through Tauri's Rust HTTP (avoids CORS) — used only
// for revocation; token calls go through the dedicated Rust commands.
const http = axios.create({ adapter: tauriAxiosAdapter, timeout: 10000 });

/**
 * Run the full browser login. Resolves to
 * `{ accessToken, refreshToken, expiresAt }` or throws. Caller shows a
 * "waiting for browser" state and may call `cancel()`.
 *
 * `provider: 'github'` makes the IdP skip its login form and send the browser
 * straight to GitHub (the app's "Continue with GitHub" button).
 */
export async function login({ provider } = {}) {
  const { invoke } = await import('@tauri-apps/api/core');
  const { open } = await import('@tauri-apps/plugin-shell');

  const state = generateOAuthState();
  const verifier = generateCodeVerifier();
  const challenge = await computeCodeChallengeS256(verifier);

  // Clear any stale listener (e.g. an abandoned request-auth flow), then bind.
  try {
    await invoke('oauth_cancel_callback');
  } catch {
    /* none active */
  }
  const port = await invoke('oauth_bind_callback', { expectedState: state });
  const redirectUri = defaultRedirectUri(port);

  let authorizeUrl = buildAuthorizeUrl({
    authUrl: `${AUTH_BASE}/oauth/authorize`,
    clientId: OIDC_CLIENT_ID,
    redirectUri,
    scope: SCOPE,
    state,
    codeChallenge: challenge,
    // Always. Signing out of the app used to leave the IdP's browser cookie
    // alive, so the next "Sign in" completed silently as the SAME account —
    // there was no way to switch accounts short of clearing browser cookies.
    // Refresh never comes through /oauth/authorize, so app restarts and silent
    // re-auth are unaffected; only an interactive sign-in asks again.
    prompt: 'login',
  });
  if (provider) {
    authorizeUrl += `&provider=${encodeURIComponent(provider)}`;
  }

  try {
    await open(authorizeUrl);
  } catch (err) {
    await invoke('oauth_cancel_callback').catch(() => {});
    throw err;
  }

  // Blocks until the browser hits the loopback callback (or 120 s timeout /
  // cancel). The Rust listener validates state and returns { code, state }.
  const { code, state: returnedState } = await invoke('oauth_await_callback');
  if (returnedState !== state) {
    throw new Error('Sign-in could not be verified. Please try again.');
  }

  const token = await invoke('oauth_exchange_token', {
    request: {
      token_url: `${AUTH_BASE}/oauth/token`,
      client_id: OIDC_CLIENT_ID,
      client_secret: null,
      code,
      redirect_uri: redirectUri,
      code_verifier: verifier,
    },
  });

  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token || null,
    expiresAt: expiresAtFromExpiresIn(token.expires_in),
  };
}

/** Cancel an in-progress browser login (releases the loopback listener). */
export async function cancel() {
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('oauth_cancel_callback');
  } catch {
    /* nothing to cancel */
  }
}

/**
 * Rotate the session: refresh tokens are single-use, so the response carries
 * a NEW refresh token the caller must persist before doing anything else.
 */
/**
 * Turn the Rust command's string rejection into something callers can act on.
 *
 * Only a `Token request failed (NNN)` prefix means the server answered and
 * rejected us — that is the one case where destroying the session is correct.
 * Everything else (DNS, connect, TLS, timeout) is the network, and the session
 * must survive it. Defaulting to ERR_NETWORK is deliberate: destroying a
 * session should require POSITIVE evidence that it is invalid, and an
 * unparseable error is not evidence.
 */
export function classifyRefreshFailure(raw) {
  const message = typeof raw === 'string' ? raw : raw?.message || String(raw);

  // Already a structured error (a future Rust change, or a non-Tauri path).
  if (raw && typeof raw === 'object' && (raw.response || raw.code)) return raw;

  // No closing paren in the pattern: Rust formats this as
  // `Token request failed (400 Bad Request): {...}`, so the status is followed
  // by the reason phrase, not by `)`.
  const httpMatch = /^Token request failed \((\d{3})\b/.exec(message);

  if (httpMatch) {
    const err = new Error(message);
    err.isAxiosError = true;
    err.response = { status: Number(httpMatch[1]), data: message };
    return err;
  }

  const err = new Error(message);
  err.isAxiosError = true;
  err.code = 'ERR_NETWORK';
  return err;
}

export async function refresh(refreshToken) {
  // The web app has no Rust HTTP client; it refreshes from the browser against
  // its own public client (services/webOidc.js), with the same error shapes.
  if (isWebApp()) return refreshWeb(refreshToken);
  const { invoke } = await import('@tauri-apps/api/core');

  // The Tauri command rejects with a PLAIN STRING for every kind of failure:
  // `post_token` in src-tauri/src/oauth.rs returns Result<TokenResponse, String>,
  // mapping transport errors through `e.to_string()` and HTTP errors through
  // `format!("Token request failed ({}), {}", status, text)`.
  //
  // That makes a DNS failure and an `invalid_grant` literally indistinguishable
  // to anything downstream, and `isNetworkError` — which requires an object —
  // returns false for both. The interceptors then clear the tokens either way,
  // which is the unrecoverable logout this whole change exists to prevent.
  //
  // So the classification happens HERE, at the boundary that knows the shape,
  // rather than by teaching a generic helper to sniff strings. The convention
  // being followed is the one tauriAxiosAdapter.js already uses for the HTTP
  // path: rebuild the failure as an AxiosError-shaped object.
  let token;

  try {
    token = await invoke('oauth_refresh_token', {
      request: {
        token_url: `${AUTH_BASE}/oauth/token`,
        client_id: OIDC_CLIENT_ID,
        client_secret: null,
        refresh_token: refreshToken,
      },
    });
  } catch (raw) {
    throw classifyRefreshFailure(raw);
  }

  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token || refreshToken,
    expiresAt: expiresAtFromExpiresIn(token.expires_in),
  };
}

/** Best-effort revocation on logout (public client — no secret needed). */
export async function revoke(refreshToken) {
  // Web app: the refresh token is the server's cookie — end that session.
  if (isWebApp()) return endWebSession();
  if (!refreshToken) return;
  try {
    const body = new URLSearchParams({
      token: refreshToken,
      token_type_hint: 'refresh_token',
      client_id: OIDC_CLIENT_ID,
    });
    await http.post(`${AUTH_BASE}/oauth/revoke`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  } catch (e) {
    console.warn('Token revocation failed (ignored):', e?.message || e);
  }
}

/** Hosted account & security page (profile, password, 2FA, passkeys, sessions). */
export function accountUrl() {
  return `${AUTH_BASE}/account`;
}

/** `jti` of a JWT access token — used to match session_replaced eviction events. */
export function peekJti(accessToken) {
  try {
    const [, payload] = String(accessToken).split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json).jti || null;
  } catch {
    return null;
  }
}
